import React, { useState, useEffect, useRef } from 'react';
import {
  Map,
  Droplets,
  Building2,
  Sun,
  BarChart3,
  Briefcase,
  GraduationCap,
  Mail,
  Linkedin,
  Phone,
  Menu,
  X,
  Database,
  Code,
  ArrowRight,
  CheckCircle2,
  Handshake,
  Send
} from 'lucide-react';

import emailjs from '@emailjs/browser';
import profilePic from "./Assests/tonny.jpg";
const Portfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [selectedProject, setSelectedProject] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Form State
  const formRef = useRef(null);
  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, error
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    message: ''
  });

  // Smooth scrolling for navigation
  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Update active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      const sections = ['home', 'about', 'experience', 'projects', 'skills', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (
          element &&
          element.offsetTop <= scrollPosition &&
          element.offsetTop + element.offsetHeight > scrollPosition
        ) {
          setActiveSection(section);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedProject) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [selectedProject]);

  // Handle Form Inputs
  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Handle Form Submission (EmailJS)
  const sendEmail = (e) => {
    e.preventDefault();
    setFormStatus('sending');

    // Replace these with your EmailJS IDs:
    // service ID, template ID, public key
    emailjs
      .sendForm(
        'service_sqm6byl',
        'template_ufwoglm',
        formRef.current,
        'wSuukYbTKoyvXx4XB'
      )
      .then(() => {
        setFormStatus('success');
        setFormData({ user_name: '', user_email: '', message: '' });
      })
      .catch((err) => {
        console.log(err);
        setFormStatus('error');
      });
  };

  const navLinks = [
    { name: 'Home', id: 'home' },
    { name: 'About', id: 'about' },
    { name: 'Experience', id: 'experience' },
    { name: 'Projects', id: 'projects' },
    { name: 'Skills', id: 'skills' },
    { name: 'Contact', id: 'contact' }
  ];

  const projects = [
    {
      id: 1,
      title: "Advanced Project Planning & Control System",
      category: "Project Controls & Scheduling",
      period: "Sep 2025 - Dec 2025",
      icon: <Code className="w-6 h-6 text-indigo-400" />,
      image:
        "https://cdn.prod.website-files.com/640bd3c70828ca85b5021dd0/640bd3c70828cae197021ee9_61eeb4cd65031c2053837f26_610c70c4f92b924ea5cf0881_1039121731-huge-4mb-p-1080.jpg",
      description:
        "A custom-built Python engine for advanced scheduling, resource optimization, and probabilistic risk analysis (Monte Carlo).",
      longDescription:
        "This project represents a sophisticated approach to construction project planning and control. Moving beyond standard scheduling software, I co-developed a Python-based system to handle advanced planning scenarios including Resource-Constrained Scheduling, Schedule Crashing (Time-Cost Trade-off), and Probabilistic Risk Assessment. The tool automates the generation of Precedence Diagram Method (PDM) networks and provides data-driven insights for decision-making.",
      challenge:
        "Complex construction projects often struggle with static schedules that fail to account for resource limitations, probabilistic risks, or the cost implications of schedule acceleration (crashing). Standard tools often require expensive add-ons for these specific analyses.",
      solution:
        "We engineered a comprehensive planning suite with distinct modules:\n\n1. Critical Path Method (CPM): Automated forward/backward pass calculations to identify critical paths and float.\n2. Resource Management: Algorithms to perform resource levelling and constrained scheduling, ensuring feasible execution plans under daily resource caps.\n3. Schedule Optimization: A 'Crashing' module that iteratively selects the lowest-cost activities to compress schedule duration.\n4. Risk Analysis: Integrated PERT and Monte Carlo simulations (1000+ iterations) to determine project completion probabilities (e.g., P80, P95 confidence levels).\n5. Repetitive Scheduling: Line of Balance (LOB) logic to optimize crew continuity for vertical/repetitive workflows.",
      achievements: [
        "Developed algorithms for Resource Constraining & Levelling to optimize workforce usage.",
        "Implemented Time-Cost Trade-off (Crashing) logic to accelerate schedules efficiently.",
        "Built Monte Carlo simulations to quantify schedule risk and generate S-curves.",
        "Applied Line of Balance (LOB) techniques for optimizing repetitive unit schedules."
      ],
      tags: [
        "Project Planning",
        "CPM/PERT",
        "Monte Carlo",
        "Resource Levelling",
        "Line of Balance",
        "Python"
      ]
    },
    {
      id: 2,
      title: "Green Space Accessibility & Urban Heat",
      category: "GIS & Machine Learning",
      period: "Sep 2024 - June 2026",
      icon: <Sun className="w-6 h-6 text-orange-400" />,
      image: "https://assets.science.nasa.gov/dynamicimage/assets/science/cds/kids/nasa-kids-science/earth/-what-is-an-urban-heat-island/Urban-heat.jpg",
      description:
        "Conducted geospatial analysis of Calgary's urban heat island (UHI) effects using GIS, satellite imagery, and machine learning to inform climate resilience.",
      longDescription:
        "This research project focuses on the intersection of urban planning and climate resilience. By leveraging satellite imagery and machine learning, the project aims to quantify the cooling effects of green spaces and assess the equity of their distribution across Calgary.",
      challenge:
        "Urban Heat Island (UHI) effects disproportionately affect certain neighborhoods. City planners need data-driven evidence to prioritize green infrastructure investments effectively.",
      solution:
        "I built a Python/Google Earth Engine (GEE) workflow that processes Landsat and Sentinel data to calculate NDVI (vegetation index) and Land Surface Temperature (LST). I applied clustering algorithms to identify 'hotspots' and correlated them with socioeconomic data to highlight accessibility gaps.",
      achievements: [
        "Built Python/GEE workflow using Landsat/Sentinel NDVI data.",
        "Produced interactive maps and KPIs for cities like Calgary, Phoenix, and Vancouver.",
        "Automated seasonal filtering and cloud thresholds to compare multi-city trends."
      ],
      tags: ["Python", "Google Earth Engine", "GIS", "Machine Learning", "Folium"]
    },
    {
      id: 3,
      title: "EACOP - Water Sourcing & Supply",
      category: "Hydraulic Design & PM",
      period: "Sep 2022 - Jul 2024",
      icon: <Droplets className="w-6 h-6 text-blue-400" />,
      image: "https://www.eacop.com/media/2024/11/5K9A7538-1.jpg",
      description:
        "Engineered and documented water-sourcing systems for construction camps and pipe yards along the 1,441 km pipeline route.",
      longDescription:
        "Served as a key Project Coordinator and Civil Engineer for the East African Crude Oil Pipeline (EACOP) project. I was responsible for the water supply infrastructure critical for the construction phase, spanning a massive 1,441 km route.",
      challenge:
        "The linear nature of the project required sourcing water in remote areas while strictly adhering to environmental regulations and social constraints. Coordination between technical design teams and local stakeholders was critical.",
      solution:
        "I modeled the water supply network using EPANET to ensure adequate pressure and flow for camps. Concurrently, I managed the Master Deliverables Register (MDR) to track over 500+ submittals, ensuring technical designs were approved on time for construction mobilization.",
      achievements: [
        "Modeled construction-water supply systems using EPANET.",
        "Packaged AutoCAD/Civil 3D drawings with geospatial overlays to reduce clashes.",
        "Liaised with community leaders to assess water availability and socio-economic impacts.",
        "Managed 500+ submittals via ACONEX/MDR controls."
      ],
      tags: ["EPANET", "Civil 3D", "AutoCAD", "Stakeholder Engagement", "MDR"]
    },
    {
      id: 4,
      title: "Lake Victoria to Dodoma Bulk Water Supply",
      category: "Infrastructure Planning",
      period: "2022 - 2024",
      icon: <Map className="w-6 h-6 text-emerald-400" />,
      image: "https://www.lvswwda.go.ke/wp-content/uploads/2021/07/Siaya-Bondo-Water-Supply-and-Sanitation-Project-750x465.jpg",
      description:
        "Feasibility and detailed design for a national-scale bulk water system (560 km trunk main) delivering water from Lake Victoria to Dodoma.",
      longDescription:
        "A national strategic project involving the design of a 560 km bulk water trunk main. My role focused on route optimization and hydraulic feasibility to transport water efficiently across varied terrain.",
      challenge:
        "Optimizing the energy consumption for pumping over a 560 km distance while navigating complex topography and minimizing land acquisition issues.",
      solution:
        "We utilized Civil 3D for route alignment and hydraulic modeling to determine the 'lowest-energy' alignment. I led corridor reconnaissance missions to verify field data against digital models, significantly de-risking the construction phase.",
      achievements: [
        "Supported route alignment and hydraulic modeling for lowest-energy alignment.",
        "Led corridor reconnaissance and data collection with utility authorities.",
        "De-risked route selection through field-verified inputs for the ~560 km concept."
      ],
      tags: ["Hydraulic Modeling", "Route Alignment", "Feasibility Studies", "Civil 3D"]
    },
    {
      id: 5,
      title: "Solar PV System (LEED Certification)",
      category: "Sustainable Energy",
      period: "Oct 2024 - Dec 2024",
      icon: <Building2 className="w-6 h-6 text-yellow-400" />,
      image:
        "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=800&q=80",
      description:
        "Developed a comprehensive project plan for a solar PV installation aligned with LEED standards.",
      longDescription:
        "A theoretical and practical application of project management principles applied to a sustainable energy project. The goal was to plan a Solar PV installation that met strict LEED certification criteria.",
      challenge:
        "Balancing the technical requirements of solar installation with the rigorous documentation and process requirements of LEED certification.",
      solution:
        "Applied Earned Value Management (EVM) techniques to simulate project tracking. Created a detailed Work Breakdown Structure (WBS) that integrated construction tasks with LEED credit documentation milestones.",
      achievements: [
        "Conducted feasibility studies and risk assessments.",
        "Applied WBS and Earned Value Management (EVM) for project tracking.",
        "Ensured alignment with sustainable building standards."
      ],
      tags: ["LEED", "Project Management", "WBS", "EVM"]
    }
  ];

  const experience = [
    {
      role: "Project Coordinator / Analyst Intern",
      company: "Gwatt Innovations Ltd (Gietron)",
      location: "Calgary, AB",
      period: "Dec 2025 - Present",
      description:
        "Focusing on Clean Energy Solutions (Solar, EV, VPP) and technical documentation.",
      details: [
        "Developing and standardizing technical documentation including product specs and installation guides.",
        "Establishing centralized documentation processes to ensure brand consistency.",
        "Conducting market research to translate technical features into clear benefit statements."
      ]
    },
    {
      role: "SANDS Intern",
      company: "University of Calgary",
      location: "Calgary, AB",
      period: "Sept 2025 - Dec 2025",
      description:
        "Spatial and Numeric Data Services internship supporting complex spatial data management.",
      details: [
        "Supported digitization and post-processing of historical archival materials.",
        "Performed rigorous quality checks and metadata management using Excel and ArcGIS Pro.",
        "Collaborated with Digital Projects Librarian on community outreach and data discoverability."
      ]
    },
    {
      role: "Project Coordinator / Civil Engineer",
      company: "Don Consult Ltd",
      location: "Dar es Salaam, Tanzania",
      period: "Sept 2022 - Aug 2024",
      description:
        "Key role in major infrastructure projects including EACOP and LVD Water Supply.",
      details: [
        "Managed master schedules for 40+ contributors and produced concise RAG status briefs for management.",
        "Supported funding/tender inputs and standardized transmittals to speed up review turnaround.",
        "Managed Master Deliverables Register (MDR) and coordinated field assessments for route siting."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-blue-100 relative">
      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div
              className="flex-shrink-0 flex items-center cursor-pointer"
              onClick={() => scrollToSection('home')}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xl mr-2 transition-colors ${
                  scrolled ? 'bg-blue-600 text-white' : 'bg-white text-blue-900'
                }`}
              >
                TC
              </div>
              <span
                className={`font-bold text-xl tracking-tight transition-colors ${
                  scrolled ? 'text-slate-900' : 'text-white'
                }`}
              >
                Tonny Chuwa
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.id)}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    activeSection === link.id
                      ? 'text-blue-400 font-bold'
                      : scrolled
                      ? 'text-slate-600 hover:text-blue-600'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {link.name}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`${scrolled ? 'text-slate-600' : 'text-white'} focus:outline-none`}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 absolute w-full shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.id)}
                  className="block w-full text-left px-3 py-4 text-base font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded-md"
                >
                  {link.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-900"
      >
        {/* Background Patterns */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            {/* Text Content */}
            <div className="flex-1 space-y-8 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-900/30 border border-blue-500/30 rounded-full text-blue-300 text-sm font-semibold tracking-wide uppercase backdrop-blur-sm shadow-lg">
                <Briefcase size={16} /> MEng Candidate | Project Management
              </div>

              <h1 className="text-5xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight drop-shadow-sm">
                Smart Engineering. <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 animate-gradient-x">
                  Strategic Management.
                </span>
              </h1>

              <p className="text-lg lg:text-xl text-slate-300 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-light">
                A Civil Engineer & MEng Candidate dedicated to optimizing workflows and solving complex challenges through data-driven decision making.
              </p>

              <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-4">
                <button
                  onClick={() => scrollToSection('projects')}
                  className="group px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl shadow-lg shadow-blue-900/50 transition-all transform hover:-translate-y-1 flex items-center gap-3"
                >
                  Explore My Work
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="px-8 py-4 bg-transparent border-2 border-slate-600 hover:border-slate-400 text-white font-semibold rounded-2xl transition-all hover:bg-slate-800/50 flex items-center gap-3"
                >
                  Contact Me
                </button>
              </div>

              <div className="pt-8 flex items-center justify-center lg:justify-start gap-8 text-slate-400 text-sm font-medium">
                <div className="flex items-center gap-2">
                  <Map size={18} className="text-blue-400" /> Calgary, AB
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={18} className="text-blue-400" /> tpchuwa@gmail.com
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="flex-1 w-full flex justify-center lg:justify-end relative">
              <div className="relative w-80 h-80 lg:w-[500px] lg:h-[500px]">
                <div className="absolute inset-0 border border-slate-700/50 rounded-full animate-[spin_10s_linear_infinite]"></div>
                <div className="absolute inset-4 border border-slate-700/30 rounded-full animate-[spin_15s_linear_infinite_reverse]"></div>

                <div className="absolute inset-8 rounded-full overflow-hidden border-4 border-slate-800 shadow-2xl bg-slate-800 relative z-20">
                  <img
                    src={profilePic}
                    alt="Tonny Chuwa"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="bg-slate-50 py-20 px-4 border-b border-slate-200">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full"></div>
          </div>
          <blockquote className="text-2xl md:text-3xl font-serif italic text-slate-800 leading-relaxed">
            "I believe great engineering isn't just about solving problems today but anticipating tomorrow's needs through data and design."
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-blue-600">
              <img
                src={profilePic}
                alt="Mini Profile"
              />
            </div>
            <div className="text-left">
              <div className="font-bold text-slate-900">Tonny Chuwa</div>
              <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">
                Civil Engineer
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section
        id="about"
        className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-cyan-600 inline-block">
              Who I Am
            </h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mt-4 rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
              <p>
                A curious problem solver at heart, I am a Civil Engineer and MEng Candidate based in Calgary, AB.
                My background spans across oil & gas, water supply, and urban planning sectors, where I thrive in dynamic environments requiring collaboration and resilience.
              </p>
              <p>
                I specialize in technical design, hydraulic modeling, and structural analysis using tools like Civil 3D and EPANET.
                Currently, I am expanding my expertise into Green Energy, GIS, and Data Analytics to build smarter, more resilient cities.
              </p>
              <p>
                Whether managing complex stakeholders on the EACOP project or modeling urban heat islands using Python,
                my goal is to deliver technical solutions that balance environmental and social objectives.
              </p>

              <div className="pt-4 flex gap-4">
                <a
                  href="https://www.linkedin.com/in/tonny-chuwa-487b17207/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 font-bold hover:text-blue-700 transition-colors"
                >
                  <Linkedin size={18} /> View LinkedIn
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <GraduationCap className="w-8 h-8 text-blue-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-1">MEng Civil Engineering</h3>
                <p className="text-sm text-slate-500">University of Calgary (2026)</p>
              </div>
              <div className="p-6 bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <GraduationCap className="w-8 h-8 text-blue-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-1">BEng Civil Engineering</h3>
                <p className="text-sm text-slate-500">Hunan University (2022)</p>
              </div>
              <div className="p-6 bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <Briefcase className="w-8 h-8 text-blue-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-1">Project Management</h3>
                <p className="text-sm text-slate-500">PMP Aspirant & Scheduling</p>
              </div>
              <div className="p-6 bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <BarChart3 className="w-8 h-8 text-blue-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-1">Data Analytics</h3>
                <p className="text-sm text-slate-500">Power BI, Excel & Python</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section
        id="experience"
        className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-slate-50 relative"
      >
        <div className="text-center mb-20">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-cyan-600 inline-block">
            Professional Experience
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="relative border-l-2 border-blue-200 ml-4 md:ml-1/2 space-y-16">
          {experience.map((job, index) => (
            <div key={index} className="relative pl-8 md:pl-0">
              <div className="absolute -left-[9px] top-0 w-5 h-5 bg-white border-4 border-blue-600 rounded-full shadow-md md:left-1/2 md:-translate-x-[10px]"></div>

              <div
                className={`md:flex items-start justify-between group ${
                  index % 2 === 0 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div
                  className={`hidden md:block w-5/12 pt-1 ${
                    index % 2 === 0 ? 'text-left' : 'text-right'
                  }`}
                >
                  <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-bold shadow-sm">
                    {job.period}
                  </span>
                </div>

                <div
                  className={`w-full md:w-5/12 bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 ${
                    index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'
                  }`}
                >
                  <div className="md:hidden mb-4">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                      {job.period}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{job.role}</h3>
                  <p className="text-blue-600 font-bold mb-3 flex items-center gap-1">
                    {job.company}{' '}
                    <span className="text-slate-400 text-sm font-normal">• {job.location}</span>
                  </p>
                  <p className="text-slate-600 mb-5 text-sm italic border-l-2 border-slate-200 pl-3">
                    {job.description}
                  </p>
                  <ul className="space-y-3">
                    {job.details.map((detail, i) => (
                      <li key={i} className="flex items-start text-sm text-slate-600">
                        <span className="mr-3 mt-1.5 w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Projects */}
      <section
        id="projects"
        className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative z-10 text-center mb-20">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-cyan-600 inline-block">
            Featured Projects
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mt-4 rounded-full"></div>
          <p className="mt-6 text-slate-600 max-w-2xl mx-auto text-lg">
            A selection of my work in hydraulic modeling, sustainable energy, and geospatial analysis.
            Click on any project to see detailed insights.
          </p>
        </div>

        <div className="relative z-10 grid md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              className="relative h-96 rounded-3xl overflow-hidden group cursor-pointer shadow-md hover:shadow-2xl transition-all duration-500"
              onClick={() => setSelectedProject(project)}
            >
              <div className="absolute inset-0">
                {project.image ? (
                  <>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent opacity-90 transition-opacity duration-300"></div>
                  </>
                ) : (
                  <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                    <Building2 className="text-slate-600 w-24 h-24" />
                  </div>
                )}
              </div>

              <div className="absolute inset-0 p-8 flex flex-col justify-end z-10 pointer-events-none">
                <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 bg-white/10 backdrop-blur-md rounded-lg text-white border border-white/20">
                      {project.icon}
                    </div>
                    <span className="text-xs font-bold text-blue-200 bg-blue-900/40 px-3 py-1 rounded-full backdrop-blur-sm border border-blue-500/30 uppercase tracking-wide">
                      {project.category}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-3 leading-tight group-hover:text-blue-300 transition-colors">
                    {project.title}
                  </h3>

                  <p className="text-slate-300 text-sm mb-6 line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100 font-medium">
                    {project.description}
                  </p>

                  <div className="flex items-center text-sm font-bold text-white group-hover:text-blue-300 transition-colors gap-2 uppercase tracking-wider">
                    View Case Study <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-slate-900/80 backdrop-blur-md transition-opacity"
            onClick={() => setSelectedProject(null)}
          ></div>

          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden relative flex flex-col z-10 animate-in fade-in zoom-in duration-300">
            <div className="relative h-48 sm:h-64 flex-shrink-0">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 text-white rounded-full backdrop-blur-md transition-colors"
              >
                <X size={24} />
              </button>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex items-center gap-2 text-blue-200 text-sm font-medium mb-2">
                  {selectedProject.icon}
                  <span>{selectedProject.category}</span>
                  <span>•</span>
                  <span>{selectedProject.period}</span>
                </div>
                <h2 className="text-3xl font-bold text-white leading-tight">
                  {selectedProject.title}
                </h2>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-8">
              <div className="grid md:grid-cols-3 gap-12">
                <div className="md:col-span-2 space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 border-l-4 border-blue-600 pl-3">
                      Project Overview
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-lg">
                      {selectedProject.longDescription}
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 shadow-sm">
                      <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2 uppercase text-xs tracking-wider">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span> The Challenge
                      </h4>
                      <p className="text-sm text-slate-600 font-medium">
                        {selectedProject.challenge}
                      </p>
                    </div>

                    <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 shadow-sm">
                      <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2 uppercase text-xs tracking-wider">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span> The Solution
                      </h4>
                      <div className="text-sm text-blue-800 whitespace-pre-wrap font-medium">
                        {selectedProject.solution}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4 border-l-4 border-green-500 pl-3">
                      Key Results & Methodologies
                    </h3>
                    <ul className="space-y-3">
                      {selectedProject.achievements.map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-3 text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-100"
                        >
                          <CheckCircle2 size={18} className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="font-medium">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                      Technologies
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-full border border-slate-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Skills */}
      <section
        id="skills"
        className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-slate-900 rounded-3xl my-12 relative overflow-hidden"
      >
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        </div>

        <div className="relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-extrabold text-white inline-block">
              Technical Proficiency
            </h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mt-4 rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700 hover:border-slate-600 transition-colors">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-900/30 border border-blue-500/20 rounded-xl flex items-center justify-center text-blue-400">
                  <Briefcase size={24} />
                </div>
                <h3 className="text-xl font-bold text-white">Project Management</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "MS Project",
                  "Primavera P6",
                  "Earned Value Mgmt (EVM)",
                  "WBS",
                  "Cost Estimation",
                  "Risk Management",
                  "CPM/PERT Scheduling",
                  "Contract Management",
                  "Procurement & Bidding",
                  "Safety Management (OHS)",
                  "Quality Assurance",
                  "ACONEX"
                ].map((skill) => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-slate-700 rounded-full text-sm text-slate-300 border border-slate-600"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="p-8 bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700 hover:border-slate-600 transition-colors">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-emerald-900/30 border border-emerald-500/20 rounded-xl flex items-center justify-center text-emerald-400">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-bold text-white">Engineering Design</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Civil 3D", "AutoCAD", "Revit", "EPANET", "Hydraulic Analysis", "Structural Analysis"].map(
                  (skill) => (
                    <span
                      key={skill}
                      className="px-3 py-1 bg-slate-700 rounded-full text-sm text-slate-300 border border-slate-600"
                    >
                      {skill}
                    </span>
                  )
                )}
              </div>
            </div>

            <div className="p-8 bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700 hover:border-slate-600 transition-colors">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-purple-900/30 border border-purple-500/20 rounded-xl flex items-center justify-center text-purple-400">
                  <Database size={24} />
                </div>
                <h3 className="text-xl font-bold text-white">Data & Technology</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "ArcGIS Pro",
                  "Python",
                  "Power BI",
                  "Excel (Advanced)",
                  "Machine Learning",
                  "Google Earth Engine",
                  "Metadata Management"
                ].map((skill) => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-slate-700 rounded-full text-sm text-slate-300 border border-slate-600"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="p-8 bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700 hover:border-slate-600 transition-colors">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-yellow-900/30 border border-yellow-500/20 rounded-xl flex items-center justify-center text-yellow-400">
                  <Handshake size={24} />
                </div>
                <h3 className="text-xl font-bold text-white">Leadership & Soft Skills</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "Stakeholder Engagement",
                  "Team Leadership",
                  "Technical Communication",
                  "Problem Solving",
                  "Adaptability",
                  "Conflict Resolution",
                  "Ethical Practice",
                  "Time Management"
                ].map((skill) => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-slate-700 rounded-full text-sm text-slate-300 border border-slate-600"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 flex flex-col md:flex-row">
          <div className="md:w-5/12 bg-slate-900 p-10 text-white flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-20"></div>

            <div className="relative z-10">
              <h2 className="text-3xl font-extrabold mb-4">Let's Connect</h2>
              <p className="text-blue-200 mb-8 leading-relaxed">
                I'm currently open to opportunities in Civil Engineering, Project Management, and Data Analytics.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    <Mail size={18} />
                  </div>
                  <span className="font-medium">tpchuwa@gmail.com</span>
                </div>

                <a
                  href="https://www.linkedin.com/in/tonny-chuwa-487b17207/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    <Linkedin size={18} />
                  </div>
                  <span className="font-medium">/in/tonny-chuwa</span>
                </a>

                <div className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    <Phone size={18} />
                  </div>
                  <span className="font-medium">+1 (368) 889-4534</span>
                </div>

                <div className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-all">
                    <Map size={18} />
                  </div>
                  <span className="font-medium">Calgary, AB</span>
                </div>
              </div>
            </div>

            <div className="mt-12 relative z-10">
              <p className="text-xs text-slate-500">© 2026 Tonny Chuwa. All Rights Reserved.</p>
            </div>
          </div>

          <div className="md:w-7/12 p-10 bg-white">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Send a Message</h3>

            <form ref={formRef} className="space-y-6" onSubmit={sendEmail}>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="Your Name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  name="user_email"
                  value={formData.user_email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Message</label>
                <textarea
                  rows="4"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="Hello Tonny, I'd like to discuss..."
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'sending'}
                className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 disabled:opacity-60"
              >
                {formStatus === 'sending' ? 'Sending...' : 'Send Message'} <Send size={18} />
              </button>

              {formStatus === 'success' && (
                <p className="text-green-600 font-medium">Message sent successfully ✅</p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-600 font-medium">
                  Failed to send. Check your EmailJS IDs and try again.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
